import * as v from 'valibot';
import type { EditTypes } from './content-definition';
import { EDITABLE_CONTENTS, EDIT_MASK_CONFIGURATIONS } from './content-definition';

type Concat<K, P> = K extends string | number ? (P extends string | number ? `${K}.${P}` : never) : never;

type Paths<T> = {
    [K in keyof T]: T[K] extends Record<string, unknown> ? Concat<K, Paths<T[K]>> : K;
}[keyof T];

export type Identifiers = Paths<typeof EDITABLE_CONTENTS>;

export interface EditValidator {
    v: v.GenericSchema<unknown, unknown, v.BaseIssue<unknown>>;
}

export function getTypeFromIdentifier(identifier: Identifiers): EditTypes | null {
    if (!identifier.includes('.')) {
        return null;
    }
    const [page, id] = identifier.split('.');
    const kPage = page as keyof typeof EDITABLE_CONTENTS;
    const kId = id as keyof (typeof EDITABLE_CONTENTS)[typeof kPage];
    return EDITABLE_CONTENTS[kPage][kId];
}

function isIdentifierValidType(identifier: Identifiers, types: EditTypes[]) {
    if (!identifier.includes('.')) {
        return false;
    }
    const validType = getTypeFromIdentifier(identifier);
    if (!validType || validType === null) {
        return false;
    }
    return types.includes(validType);
}

export const vValidIdentifier = (...types: EditTypes[]) =>
    v.custom<Identifiers>(
        (val) => isIdentifierValidType(val as Identifiers, types),
        `Invalid identifier for type ${types.join(', ')}`,
    );

export const getValidators = (identifier: Identifiers): EditMaskConfig['validator'] | null => {
    const validator = EDIT_MASK_CONFIGURATIONS[identifier]?.validator;
    if (!validator) {
        return null;
    }
    return validator;
};

export const getEditMasksTexts = (identifier: Identifiers) => {
    const headline = EDIT_MASK_CONFIGURATIONS[identifier]?.headline || null;
    const subHeadline = EDIT_MASK_CONFIGURATIONS[identifier]?.subHeadline || null;
    const validatorHint = EDIT_MASK_CONFIGURATIONS[identifier]?.validatorHint || null;
    return { headline, subHeadline, validatorHint };
};

export const getEditMasksImageRequirements = (identifier: Identifiers) => {
    return EDIT_MASK_CONFIGURATIONS[identifier]?.imageRequirements || null;
};

export function validateEdit<T>(identifier: Identifiers, value: T) {
    const validator = getValidators(identifier);
    return validator ? v.safeParse(validator, value).success : true;
}

export function editValidation(...editTypes: EditTypes[]) {
    return v.pipe(
        v.object({
            identifier: vValidIdentifier(...editTypes),
            value: v.string(),
        }),
        v.forward(
            v.check(({ identifier, value }) => validateEdit(identifier, value)),
            ['value'],
        ),
    );
}

async function isFileTypeAllowed(file: File, types: string[]): Promise<boolean> {
    if ((types.length === 0 && !file.type.startsWith('image/')) || (types.length > 0 && !types.includes(file.type))) {
        throw new Error('Dateiformat ist nicht erlaubt');
    }
    return true;
}

async function isFileSizeAllowed(file: File, maxFileSize: number): Promise<boolean> {
    if (maxFileSize) return true;
    if (file.size > maxFileSize) {
        throw new Error('Datei ist zu groß');
    }
    return true;
}

export async function areImageDimensionsAllowed(
    width: number,
    height: number,
    reqs: ImageRequirements,
): Promise<boolean> {
    const errors: string[] = [];
    if (reqs.minWidth && width < reqs.minWidth) errors.push('zu kleine Breite');
    if (reqs.minHeight && height < reqs.minHeight) errors.push('zu kleine Höhe');
    if (reqs.maxWidth && width > reqs.maxWidth) errors.push('zu große Breite');
    if (reqs.maxHeight && height > reqs.maxHeight) errors.push('zu große Höhe');

    if (errors.length > 0) {
        throw new Error(`Bild hat ${errors.join(' und ')}`);
    }

    return true;
}

export async function validateImageRequirements(
    identifier: Identifiers,
    file: File,
    dimensions?: { width: number; height: number },
): Promise<boolean> {
    const imageRequirements = getEditMasksImageRequirements(identifier);
    await isFileTypeAllowed(file, imageRequirements?.types?.map((t) => `image/${t === 'jpg' ? 'jpeg' : t}`) || []);
    if (!imageRequirements) {
        return true;
    }

    if (imageRequirements.maxFileSize) {
        await isFileSizeAllowed(file, imageRequirements.maxFileSize);
    }

    if (
        dimensions &&
        (imageRequirements.maxWidth ||
            imageRequirements.maxHeight ||
            imageRequirements.minWidth ||
            imageRequirements.minHeight)
    ) {
        await areImageDimensionsAllowed(dimensions.width, dimensions.height, imageRequirements);
    }

    return true;
}

export interface ImageRequirements {
    types?: Array<'jpg' | 'png'>;
    maxFileSize?: number;
    maxWidth?: number;
    maxHeight?: number;
    minWidth?: number;
    minHeight?: number;
    aspectRatio?: string;
}

export type EditMaskConfig = {
    validator?: v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>;
    imageRequirements?: ImageRequirements;
    headline?: string;
    subHeadline?: string;
    validatorHint?: string;
    locationAware?: boolean;
};
